import "mapbox-gl/dist/mapbox-gl.css";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";

import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import { json } from "@remix-run/node";
import { useLoaderData } from "@remix-run/react";
import type { User } from "@supabase/supabase-js";
import { Provider } from "jotai";
import {
  FullscreenControl,
  GeoLocationControl,
  ResetControl,
  ZoomControl,
  HydrateAtoms,
} from "~/components";
import {
  BoundaryLayer,
  CategoryPills,
  CenteredMessage,
  InfoWindow,
  MapEmbed,
  MapInformation,
  MapSearch,
  MapWrapper,
  MarkerLayer,
  PasswordProtected,
  PublicShare,
  SidebarList,
} from "~/components/embedded";
import { arrayToKeyed } from "~/lib/array-to-object";
import { getAuthSession } from "~/lib/session.server";
import { createAdminClient } from "~/lib/supabase.server";
import { createMapStyle } from "~/lib/themeHelpers";
import {
  categoriesAtom,
  featuresAtom,
  mapEntryAtom,
  mapFiltersAtom,
  styleConfigAtom,
} from "~/stores";
import dayjs from "dayjs";
import { IconLoader2 } from "@tabler/icons-react";
import { trpc } from "~/lib/trpc";
import LeadForm from "~/components/embedded/LeadForm";
import { GeolocationMarker } from "~/components/GeolocationControl";
import { cn } from "~/lib/utils";

export async function loader({ request, params }: LoaderFunctionArgs) {
  const supabase = createAdminClient();
  const { data: critical } = await supabase
    .from("maps")
    .select("id, workspace_id, access, created_by, title, updated_at")
    .match({ id: params.mapId })
    .single();

  if (!critical) {
    throw new Response("", {
      status: 404,
      statusText: "Map not found",
    });
  }

  // generate og image url
  const path = `open-graph/${critical.workspace_id}-${critical.id}.png`;
  const { data } = supabase.storage.from("images").getPublicUrl(path);
  const ogVersion = dayjs(critical.updated_at).unix();

  const authSession = await getAuthSession(request);
  let user: User | null = null;
  if (authSession?.session) {
    // user is logged in
    const { data } = await supabase.auth.getUser(
      authSession.session.access_token
    );
    if (data) {
      user = data.user;
    }
  }

  return json({
    title: critical?.title as string,
    id: params.mapId as string,
    user,
    openGraph: `${data.publicUrl}?v=${ogVersion}`,
  });
}

export const meta: MetaFunction<typeof loader> = ({ data, matches }) => {
  return [
    { title: `${data?.title} - Carta Maps` },
    { property: "og:title", content: `${data?.title} - Carta Maps` },
  ];
};

export const handle = {
  context: "embed",
};

export default function EmbedMapPage() {
  const { id } = useLoaderData<typeof loader>();

  const { data, error, isLoading } = trpc.embeds.loadMapData.useQuery(
    { map_id: id, type: "embed" },
    {
      retry: false,
    }
  );

  if (isLoading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-gray-100 text-slate-800">
        <IconLoader2 size={48} className="animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <CenteredMessage>
        <div>
          <h1 style={{ fontWeight: 900 }}>Sorry</h1>
          <p>{error.message}</p>
        </div>
      </CenteredMessage>
    );
  }

  if (data?.access === "private") {
    return (
      <CenteredMessage>
        <div className="text-6xl will-change-transform hover:animate-bounce">
          🔐
        </div>
        <div>
          <h1 style={{ fontWeight: 900 }}>Sorry!</h1>
          <p>This map is private.</p>
        </div>
      </CenteredMessage>
    );
  }

  if (data?.access === "protected" && !data?.authPassed) {
    return <PasswordProtected uuid={data.entry.id} />;
  }

  if (data?.vieweable !== true) {
    return null;
  }

  const {
    controls: { zoomControl, geolocationControl, fullscreenControl },
    styles: {
      preview: { showSidebar },
    },
  } = data.entry;

  return (
    <Provider key={data.entry.id}>
      <HydrateAtoms
        initialValues={[
          [mapEntryAtom, data.entry],
          [mapFiltersAtom, arrayToKeyed(data.filters ?? [], "id")],
          [featuresAtom, arrayToKeyed(data.features ?? [], "id")],
          [categoriesAtom, arrayToKeyed(data.categories ?? [], "id")],
          [
            styleConfigAtom,
            typeof data.styleConfig === "string"
              ? data.styleConfig
              : createMapStyle(data.styleConfig!),
          ],
        ]}
      >
        <div className="bg-gray-50 h-screen relative flex flex-col carta-map">
          <main className="flex flex-1 map__container">
            <MapWrapper showSidebar={showSidebar}>
              <MapEmbed
                defaultBounds={
                  data.bounds?.length
                    ? (data.bounds as [number, number, number, number])
                    : undefined
                }
                style={{ position: "fixed", inset: 0 }}
              >
                <div className="absolute inset-0 grid grid-cols-[320px_1fr] gap-4 p-4 pointer-events-none">
                  <div
                    className={cn(
                      "pointer-events-auto hidden md:block relative z-50",
                      !showSidebar && "!hidden"
                    )}
                  >
                    <SidebarList showSidebar={showSidebar} />
                  </div>
                  <div
                    className={cn(
                      "grid grid-rows-[64px_1fr] gap-4",
                      !showSidebar ? "col-span-2" : "col-span-2 md:col-span-1"
                    )}
                  >
                    <div className="flex items-start">
                      <div className="flex items-center gap-x-2">
                        <MapInformation
                          showSidebar={showSidebar}
                          openGraph={""}
                        />
                        <MapSearch showSidebar={showSidebar} />
                        <CategoryPills />
                      </div>
                      <div className="flex items-center gap-x-1 ml-auto">
                        <LeadForm slot="global" />
                        <LeadForm slot="crowdsource" />
                      </div>
                    </div>
                    <div className="relative z-[999]">
                      <InfoWindow />
                    </div>
                  </div>
                </div>

                <div className="absolute bottom-1/2 transform translate-y-1/2 lg:translate-y-0 lg:bottom-6 right-4 flex flex-col gap-y-2 z-50">
                  {geolocationControl && <GeoLocationControl />}
                  {zoomControl && (
                    <ZoomControl className="flex-col gap-x-0 gap-y-3" />
                  )}
                  <ResetControl />
                  {fullscreenControl && <FullscreenControl />}
                  <PublicShare mediaUrl={""} />
                </div>

                <MarkerLayer />
                <BoundaryLayer />
                {/* <InfoWindow /> */}
                <GeolocationMarker />
              </MapEmbed>
            </MapWrapper>
          </main>
        </div>
      </HydrateAtoms>
    </Provider>
  );
}
